import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import marketsApi from "@api/markets";
import { setMarketsCatalogSnapshot } from "@core/utils/marketsCatalogStore";

const emptyCatalog = {
  quoteCurrencies: [],
  marketsByQuote: {},
  markets: [],
  currencies: {},
};

export const MarketsContext = createContext(null);

export function MarketsProvider({ children }) {
  const [catalog, setCatalog] = useState(emptyCatalog);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  const loadCatalog = useCallback(() => {
    return marketsApi
      .getCatalog()
      .then((res) => {
        const content = res?.content || emptyCatalog;
        setCatalog(content);
        setMarketsCatalogSnapshot(content);
        setIsLoaded(true);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setIsLoaded(true);
      });
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const marketByRoute = useMemo(() => {
    const map = new Map();
    (catalog.markets || []).forEach((m) => {
      map.set(m.route, m);
    });
    return map;
  }, [catalog.markets]);

  const getMarketByRoute = useCallback(
    (route) => marketByRoute.get(route) || null,
    [marketByRoute]
  );

  const getBasesForQuote = useCallback(
    (quote) => catalog.marketsByQuote?.[String(quote || "").toUpperCase()] || [],
    [catalog.marketsByQuote]
  );

  const defaultMarketRoute = useMemo(() => {
    const quotes = catalog.quoteCurrencies || [];
    for (const quote of quotes) {
      const bases = catalog.marketsByQuote?.[quote] || [];
      if (bases.length > 0) {
        return `${bases[0]}-${quote}`;
      }
    }
    return null;
  }, [catalog]);

  const value = useMemo(
    () => ({
      catalog,
      isLoaded,
      error,
      reloadCatalog: loadCatalog,
      quoteCurrencies: catalog.quoteCurrencies || [],
      marketsByQuote: catalog.marketsByQuote || {},
      markets: catalog.markets || [],
      getMarketByRoute,
      getBasesForQuote,
      defaultMarketRoute,
    }),
    [
      catalog,
      isLoaded,
      error,
      loadCatalog,
      getMarketByRoute,
      getBasesForQuote,
      defaultMarketRoute,
    ]
  );

  return (
    <MarketsContext.Provider value={value}>{children}</MarketsContext.Provider>
  );
}

export function useMarkets() {
  const ctx = useContext(MarketsContext);
  if (!ctx) {
    throw new Error("useMarkets must be used within MarketsProvider");
  }
  return ctx;
}
